import { useEffect, useState } from "react";
import { api, formatApiError, fmtRub } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash, Warning } from "@phosphor-icons/react";

const categories = ["Продукты", "Транспорт", "Жильё", "Развлечения", "Здоровье", "Одежда", "Кафе", "Другое"];
const barColor = (pct) => (pct >= 100 ? "bg-[#ff3b30]" : pct >= 80 ? "bg-[#ffea00]" : "bg-[#00e676]");

export default function BudgetLimits({ month, refreshKey }) {
  const [limits, setLimits] = useState([]);
  const [form, setForm] = useState({ category: "Продукты", amount: "" });

  const load = () => api.get(`/budget/limits?month=${month}`).then((r) => setLimits(r.data)).catch(() => {});
  useEffect(() => { load(); }, [month, refreshKey]); // eslint-disable-line

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/budget/limits", { category: form.category, amount: parseFloat(form.amount) });
      setForm((f) => ({ ...f, amount: "" }));
      toast.success("Лимит сохранён");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (category) => {
    await api.delete(`/budget/limits/${encodeURIComponent(category)}`);
    load();
  };

  return (
    <div className="glass-card p-6" data-testid="budget-limits-card">
      <h2 className="text-xs uppercase tracking-widest text-muted-foreground mb-4">Лимиты расходов</h2>
      <form onSubmit={save} className="flex gap-2 mb-4">
        <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
          <SelectTrigger className="flex-1" data-testid="limit-category-select"><SelectValue /></SelectTrigger>
          <SelectContent className="glass-modal">
            {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input data-testid="limit-amount-input" type="number" min="1" step="1" value={form.amount}
          onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} placeholder="₽" required className="w-28" />
        <Button type="submit" size="icon" data-testid="limit-submit-button"><Plus size={16} /></Button>
      </form>
      {limits.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="limits-empty">Установите лимит по категории — и следите за прогрессом</p>
      ) : (
        <div className="space-y-3">
          {limits.map((l) => (
            <div key={l.category} data-testid={`limit-row-${l.category}`}>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="flex items-center gap-1.5">
                  {l.category}
                  {l.pct >= 100 && (
                    <span className="flex items-center gap-0.5 text-[10px] uppercase tracking-wider font-semibold text-[#ff3b30]" data-testid={`limit-exceeded-${l.category}`}>
                      <Warning size={12} weight="fill" /> Превышен
                    </span>
                  )}
                </span>
                <span className="flex items-center gap-2">
                  <span className="num text-xs text-muted-foreground">{fmtRub(l.spent)} / {fmtRub(l.limit)}</span>
                  <button onClick={() => remove(l.category)} data-testid={`limit-delete-${l.category}`} className="text-muted-foreground hover:text-destructive transition-colors"><Trash size={14} /></button>
                </span>
              </div>
              <div className="h-2 bg-secondary rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${barColor(l.pct)}`} style={{ width: `${Math.min(l.pct, 100)}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
